/**
 * Computes a fuzzy match score for a query against a target string.
 *
 * Returns a score >= 0 if the query fuzzy-matches the target, or -1 if there
 * is no match. A higher score indicates a better match. The algorithm rewards:
 * - Consecutive character matches
 * - Matches at the start of the string
 * - Matches at the start of words (after spaces/hyphens)
 *
 * Both query and target are compared case-insensitively.
 */
export function fuzzyScore(query: string, target: string): number {
  const q = query.toLowerCase();
  const t = target.toLowerCase();

  if (q.length === 0) return 0;
  if (q.length > t.length) return -1;

  let score = 0;
  let qi = 0;
  let prevMatchIndex = -2;

  for (let ti = 0; ti < t.length && qi < q.length; ti++) {
    if (t[ti] === q[qi]) {
      // Base point for matching
      score += 1;

      // Bonus for consecutive matches
      if (ti === prevMatchIndex + 1) {
        score += 3;
      }

      // Bonus for matching at the start of the string
      if (ti === 0) {
        score += 5;
      }

      // Bonus for matching at the start of a word (after space, hyphen, underscore)
      if (ti > 0) {
        const prev = t[ti - 1];
        if (prev === ' ' || prev === '-' || prev === '_') {
          score += 3;
        }
      }

      prevMatchIndex = ti;
      qi++;
    }
  }

  // All query characters must be found in order
  if (qi < q.length) return -1;

  return score;
}

/**
 * Filters and sorts items by fuzzy matching a query against a string extracted
 * from each item. Returns only items that match, sorted by best match first.
 */
export function fuzzySearch<T>(
  items: readonly T[],
  query: string,
  getText: (item: T) => string,
): T[] {
  const trimmed = query.trim();
  if (trimmed.length === 0) {
    return [...items];
  }

  return items
    .map(item => ({ item, score: fuzzyScore(trimmed, getText(item)) }))
    .filter(({ score }) => score >= 0)
    .sort((a, b) => b.score - a.score)
    .map(({ item }) => item);
}
